import { FlexCenter } from '@elementary/common-ui/components';
import { CircularProgress, styled } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DATA_HEALTH_QUALITY_DIMENSION_THRESHOLDS } from '@elementary/common-ui/constants/queryKeys';
import { Controller, useForm } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { parseIntegrationError } from 'settings/utils';
import * as api from '../../services/api';
import { ScoreThresholdsForm } from '../../types';
import DataHealthDialog from '../shared/DataHealthDialog';
import ScoreThresholdsSlider from '../shared/ScoreThresholdsSlider';

type Props = {
  title: string;
  description: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
};

const CenteringWrapper = styled(FlexCenter)`
  height: 100%;
`;

const ScoreThresholdsDialog = ({
  title,
  description,
  open,
  onClose,
  onSave,
}: Props) => {
  const selectedEnvId = useSelectedEnv();

  const { data, isError, isFetching } = useQuery(
    [DATA_HEALTH_QUALITY_DIMENSION_THRESHOLDS],
    () =>
      api.fetchQualityDimensionThresholds(selectedEnvId!, title.toLowerCase()),
    { staleTime: 0 },
  );

  const {
    mutate,
    isError: updateScoreThresholdsError,
    isLoading: isMutateLoading,
  } = useMutation(
    (scoreFormula: ScoreThresholdsForm) =>
      api.updateQualityDimensionThresholds(
        selectedEnvId!,
        title.toLowerCase(),
        Math.min(...scoreFormula.sliderValues),
        Math.max(...scoreFormula.sliderValues),
      ),
    {
      onSuccess: () => {
        onSave();
      },
      onError: () => {
        onClose();
      },
    },
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ScoreThresholdsForm>({
    values: {
      sliderValues: data ? [data?.low, data?.medium] : [],
    },
    mode: 'onBlur',
  });

  return (
    <DataHealthDialog
      width="700px"
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit((formData: ScoreThresholdsForm) =>
        mutate(formData),
      )}
      error={
        parseIntegrationError(errors) ||
        parseIntegrationError(isError) ||
        parseIntegrationError(updateScoreThresholdsError)
      }
      loading={isMutateLoading}
      title={title}
      description={description}
      isValid={isValid || !isFetching}
    >
      {isFetching === true && (
        <CenteringWrapper>
          <CircularProgress />
        </CenteringWrapper>
      )}

      {isFetching === false && data !== undefined && (
        <Controller
          render={({ field }) => (
            <ScoreThresholdsSlider
              sliderValues={field.value}
              onChange={field.onChange}
            />
          )}
          name="sliderValues"
          control={control}
        />
      )}
    </DataHealthDialog>
  );
};

export default ScoreThresholdsDialog;
